import { isAxiosError } from 'axios';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import IconErrorAlt from '~icons/bx/error-alt';
import { ErrorIndicator, LoadingIndicator, SimpleEmptySection } from '@arc/frontend/ui-graphics';
import { useDelayUnmount } from '@arc/frontend/util-common';
import { handleRequestErrors } from './handle-request-errors';
/**
 * Handle repetitive logic of displaying UI for components that use `useQuery`.
 *
 * Returns a `JSX.Element` to display if any queries are in the loading or error states.
 * Returns `null` if all queries are loaded and ok.
 *
 * @example
 * function MyComponent(props) {
 *   const queryResult = useQuery(whatever);
 *   const antherQueryResult = useQuery(whatever);
 *
 *   const queryStatusIndicator = useQueryStatusUI(
 *     [queryResult, antherQueryResult],
 *     { errorMessage: 'Oh no! Could not load data.' }
 *   );
 *
 *   // One or more queries do not have their data ready.
 *   // Display a status indicator.
 *   if (queryStatusIndicator !== null) {
 *     return queryStatusIndicator;
 *   }
 *
 *   // All the data is ready. Now render the normal UI.
 *   return <div>{queryResult.data}</div>
 * }
 *
 * @param queryResults Array of return values of react-query's `useQuery`.
 */
export function useQueryStatusUI(queryResults, options) {
  const {
    errorMessage,
    loadingDelay,
    waitUntilFetchedAfterMount,
    showNotFoundIndicator,
    notFoundError
  } = options;
  const {
    t
  } = useTranslation('common');
  const isLoading = queryResults.some(x => waitUntilFetchedAfterMount && x.isStale ? !x.isFetchedAfterMount : x.isPending && x.fetchStatus === 'fetching');
  const showIndicator = useDelayUnmount(!isLoading, loadingDelay ?? 0);
  const isError = queryResults.some(x => x.isError);
  const isNotFoundError = isError && queryResults.filter(x => x.isError).every(x => isAxiosError(x.error) && x.error?.response?.status === 404);
  useEffect(() => {
    if (!isLoading && isError) {
      const errors = queryResults.map(res => res.error);
      if (isNotFoundError && showNotFoundIndicator) {
        return;
      }
      handleRequestErrors(errors, {
        errorMessage
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError, isLoading, errorMessage, isNotFoundError]);
  if (showIndicator && loadingDelay) {
    return null;
  }
  if (isLoading) {
    return <LoadingIndicator />;
  }
  if (isNotFoundError && showNotFoundIndicator) {
    return <SimpleEmptySection Icon={IconErrorAlt} message={notFoundError ?? t('notFound')} />;
  }
  if (isError) {
    return <ErrorIndicator />;
  }
  return null;
}